import { CommonModule } from '@angular/common';
import { Component, ViewChild, output } from '@angular/core';
import { IWorkoutPlanJob, WorkoutPlanJobStatus } from '@foxera/shared/interfaces/client.interface';
import { PermissionService } from '@foxera/shared/services/permission.service';
import { WorkoutJobsStateService } from '@foxera/shared/services/workout-jobs-state.service';
import { Popover, PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-workout-jobs-popover',
  standalone: true,
  imports: [CommonModule, PopoverModule, TooltipModule],
  templateUrl: './workout-jobs-popover.component.html',
  styleUrls: ['./workout-jobs-popover.component.css'],
})
export class WorkoutJobsPopoverComponent {
  @ViewChild('workoutJobsOverlay') workoutJobsOverlay!: Popover;

  readonly jobClick = output<IWorkoutPlanJob>();
  readonly cancelJob = output<IWorkoutPlanJob>();
  readonly retryJob = output<IWorkoutPlanJob>();

  constructor(
    public workoutJobsStateService: WorkoutJobsStateService,
    public permissionService: PermissionService
  ) {}

  toggle(event: MouseEvent): void {
    this.workoutJobsOverlay?.toggle(event);
  }

  hide(): void {
    this.workoutJobsOverlay?.hide();
  }

  onJobClick(job: IWorkoutPlanJob): void {
    if (job.status === 'Failed' || job.status === 'Cancelled') return;
    this.jobClick.emit(job);
    this.hide();
  }

  onCancelJob(event: MouseEvent, job: IWorkoutPlanJob): void {
    event.stopPropagation();
    if (!this.permissionService.canAddWorkoutPlan()) return;
    this.cancelJob.emit(job);
  }

  onRetryJob(event: MouseEvent, job: IWorkoutPlanJob): void {
    event.stopPropagation();
    if (!this.permissionService.canAddWorkoutPlan()) return;
    this.retryJob.emit(job);
  }

  getJobStatusLabel(status: WorkoutPlanJobStatus): string {
    const labels: Record<WorkoutPlanJobStatus, string> = {
      Pending: 'Generating…',
      Processing: 'Generating…',
      Completed: 'Ready',
      Failed: 'Failed',
      Cancelled: 'Cancelled',
    };
    return labels[status];
  }

  isJobActive(job: IWorkoutPlanJob): boolean {
    return job.status === 'Pending' || job.status === 'Processing';
  }

  isJobClickable(job: IWorkoutPlanJob): boolean {
    return job.status === 'Pending' || job.status === 'Processing' || job.status === 'Completed';
  }

  /** Human-readable elapsed time for finished jobs, e.g. "42s", "1m 41s" */
  formatElapsedDuration(durationInSeconds: number): string {
    const totalSeconds = Math.floor(durationInSeconds);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes === 0) return `${seconds}s`;
    if (seconds === 0) return `${minutes}m`;
    return `${minutes}m ${seconds}s`;
  }
}
